import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { SERVICES, SERVICE_CATEGORIES, INDUSTRIES } from "@/data/content";

const linkBase = "text-sm tracking-wide font-medium transition-colors whitespace-nowrap";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(null); // 'services' | 'industries' | null

  const closeAll = () => { setOpen(false); setMega(null); };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[var(--dm-border)]"
    >
      <div className="dm-container flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={closeAll}>
          <Logo tone="dark" size={38} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-7" onMouseLeave={() => setMega(null)}>
          <NavLink to="/" end data-testid="nav-home" className={({isActive}) => `${linkBase} ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]'}`}>Home</NavLink>
          <NavLink to="/about" data-testid="nav-about" className={({isActive}) => `${linkBase} ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]'}`}>About</NavLink>

          <button
            data-testid="nav-services-trigger"
            onMouseEnter={() => setMega('services')}
            className={`${linkBase} inline-flex items-center gap-1 text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]`}
          >Services <ChevronDown className="w-3.5 h-3.5" /></button>

          <button
            data-testid="nav-industries-trigger"
            onMouseEnter={() => setMega('industries')}
            className={`${linkBase} inline-flex items-center gap-1 text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]`}
          >Industries <ChevronDown className="w-3.5 h-3.5" /></button>

          <NavLink to="/case-studies" data-testid="nav-cases" className={({isActive}) => `${linkBase} ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]'}`}>Case Studies</NavLink>
          <NavLink to="/technologies" data-testid="nav-tech" className={({isActive}) => `${linkBase} ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]'}`}>Technologies</NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={({isActive}) => `${linkBase} ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]'}`}>Blog</NavLink>
          <NavLink to="/careers" data-testid="nav-careers" className={({isActive}) => `${linkBase} ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]'}`}>Careers</NavLink>
          <NavLink to="/admin/sign-in" data-testid="nav-admin" className={({isActive}) => `${linkBase} ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)] hover:text-[var(--dm-emerald)]'}`}>Admin</NavLink>
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <Link to="/contact" data-testid="nav-cta-contact" className="dm-btn-primary whitespace-nowrap">Talk to a Specialist</Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="xl:hidden p-2 text-[var(--dm-navy)]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mega menu */}
      {mega === 'services' && (
        <div
          data-testid="nav-services-mega"
          onMouseEnter={() => setMega('services')}
          onMouseLeave={() => setMega(null)}
          className="hidden xl:block border-t border-[var(--dm-border)] bg-white"
        >
          <div className="dm-container grid grid-cols-5 gap-8 py-10">
            {SERVICE_CATEGORIES.map((cat) => (
              <div key={cat.id}>
                <div className="dm-overline mb-3">{cat.name}</div>
                <ul className="space-y-2">
                  {SERVICES.filter((s) => s.category === cat.id).map((s) => (
                    <li key={s.slug}>
                      <Link
                        to={`/services/${s.slug}`}
                        onClick={closeAll}
                        data-testid={`nav-service-${s.slug}`}
                        className="text-sm text-[var(--dm-navy)] hover:text-[var(--dm-emerald)] block"
                      >{s.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {mega === 'industries' && (
        <div
          data-testid="nav-industries-mega"
          onMouseEnter={() => setMega('industries')}
          onMouseLeave={() => setMega(null)}
          className="hidden xl:block border-t border-[var(--dm-border)] bg-white"
        >
          <div className="dm-container grid grid-cols-4 gap-6 py-10">
            {INDUSTRIES.map((i) => (
              <Link
                key={i.slug}
                to={`/industries/${i.slug}`}
                onClick={closeAll}
                data-testid={`nav-industry-${i.slug}`}
                className="group block"
              >
                <div className="text-sm font-semibold text-[var(--dm-navy)] group-hover:text-[var(--dm-emerald)]">{i.name}</div>
                <div className="text-xs text-[var(--dm-muted)] mt-1 leading-relaxed">{i.summary}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      {open && (
        <div data-testid="nav-mobile-panel" className="xl:hidden border-t border-[var(--dm-border)] bg-white">
          <div className="dm-container py-6 grid gap-4">
            {["/", "/about", "/services", "/industries", "/case-studies", "/technologies", "/blog", "/careers", "/admin/sign-in", "/contact"].map((p, idx) => (
              <NavLink key={p} to={p} end={p === "/"} onClick={closeAll}
                data-testid={p === "/admin/sign-in" ? "nav-m-admin" : `nav-m-${idx}`}
                className={({isActive}) => `text-base font-medium ${isActive ? 'text-[var(--dm-emerald)]' : 'text-[var(--dm-navy)]'}`}
              >{p === "/" ? "Home" : p === "/admin/sign-in" ? "Admin" : p.replace("/", "").replace(/-/g, " ").replace(/^\w/, c => c.toUpperCase())}</NavLink>
            ))}
            <Link to="/contact" onClick={closeAll} data-testid="nav-m-cta" className="dm-btn-primary mt-2">Talk to a Specialist</Link>
          </div>
        </div>
      )}
    </header>
  );
}
